import {
  Button, Card, DataTable, EmptyState, Field, Input, LoadingState, Notice, Row, Select, Stack,
  describeError, useApi, useI18n,
  type CloseAcademicYearRequest, type CloseAcademicYearResponse, type Column, type RolloverChildPreview,
  type RolloverClassPreview, type RolloverPreviewResponse,
} from '@daghfal/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';

/** A child's decision in the form: graduate, the copy of a class of this year, or a class the next year has. */
type Decision = 'graduate' | `copy:${number}` | `class:${number}`;

interface ClassPlan {
  carried: boolean;
  name: string;
}

/**
 * The end of the academic year (Q36). The API proposes the next year's classes, as copies of this year's, and
 * each child's place in them by age; the administration reviews and changes what it likes, then closes the year
 * in one step. Shows children's names, so the shell draws the watermark over it.
 */
export function Rollover() {
  const api = useApi();
  const { t } = useI18n();
  const yearId = Number(useParams().id);

  const preview = useQuery({
    queryKey: ['academic-years', yearId, 'rollover-preview'],
    queryFn: () => api.academicYears.rolloverPreview(yearId),
  });

  return (
    <>
      <div className="dg-page-header">
        <div className="dg-page-header__row">
          <h1>{preview.data ? t('rollover.title', { year: preview.data.yearName }) : t('rollover.title.plain')}</h1>
          <Link className="dg-button dg-button--ghost" to="/academic-years">{t('action.back')}</Link>
        </div>
        <p className="dg-page-header__lede">{t('rollover.lede')}</p>
      </div>

      {preview.isPending && <LoadingState />}
      {preview.isError && <ErrorNotice error={preview.error} />}
      {preview.data && <RolloverForm key={preview.data.yearId} preview={preview.data} />}
    </>
  );
}

function RolloverForm({ preview }: { preview: RolloverPreviewResponse }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const { t, tn, formatDate, today } = useI18n();

  const [classes, setClasses] = useState<Record<number, ClassPlan>>(() =>
    Object.fromEntries(preview.classes.map((c) => [c.id, { carried: true, name: c.name }])));
  const [decisions, setDecisions] = useState<Record<number, Decision>>(() =>
    Object.fromEntries(preview.children.map((c) => [c.childId, proposed(c)])));

  const close = useMutation({
    mutationFn: () => api.academicYears.close(preview.yearId, plan(preview, classes, decisions)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['academic-years'] });
      void queryClient.invalidateQueries({ queryKey: ['classes'] });
      void queryClient.invalidateQueries({ queryKey: ['children'] });
    },
  });

  if (close.data) return <Done result={close.data} preview={preview} />;

  const ended = today() >= preview.canCloseFrom;
  const setClass = (id: number, change: Partial<ClassPlan>) =>
    setClasses((current) => ({ ...current, [id]: { ...planFor(current, id), ...change } }));
  const className = (id: number) => preview.classes.find((c) => c.id === id)?.name ?? '';
  const undecided = preview.children.filter((c) => !valid(decisions[c.childId], classes));

  const classColumns: Column<RolloverClassPreview>[] = [
    {
      key: 'carry',
      header: t('rollover.class.carry'),
      render: (c) => (
        <input
          type="checkbox"
          aria-label={t('rollover.class.carryOne', { name: c.name })}
          checked={planFor(classes, c.id).carried}
          onChange={(e) => setClass(c.id, { carried: e.target.checked })}
          style={{ inlineSize: 20, blockSize: 20 }}
        />
      ),
    },
    { key: 'name', header: t('rollover.class.now'), render: (c) => c.name },
    {
      key: 'next',
      header: t('rollover.class.next', { year: preview.nextYearName }),
      render: (c) => (
        <Input
          aria-label={t('rollover.class.next', { year: preview.nextYearName })}
          value={planFor(classes, c.id).name}
          disabled={!planFor(classes, c.id).carried}
          onChange={(e) => setClass(c.id, { name: e.target.value })}
        />
      ),
    },
    { key: 'children', header: t('rollover.class.children'), numeric: true, render: (c) => c.childCount },
  ];

  const childColumns: Column<RolloverChildPreview>[] = [
    { key: 'name', header: t('rollover.child.name'), render: (c) => c.name },
    { key: 'from', header: t('rollover.child.from'), render: (c) => className(c.fromClassId) },
    { key: 'age', header: t('rollover.child.age'), render: (c) => tn('rollover.months', c.ageMonths) },
    {
      key: 'to',
      header: t('rollover.child.to'),
      render: (c) => (
        <Select
          aria-label={t('rollover.child.toOne', { name: c.name })}
          value={decisions[c.childId] ?? ''}
          onChange={(e) => setDecisions((current) => ({ ...current, [c.childId]: e.target.value as Decision }))}
        >
          {preview.classes.filter((source) => planFor(classes, source.id).carried).map((source) => (
            <option key={`copy-${source.id}`} value={`copy:${source.id}`}>{planFor(classes, source.id).name}</option>
          ))}
          {preview.nextYearClasses.map((existing) => (
            <option key={`class-${existing.id}`} value={`class:${existing.id}`}>{existing.name}</option>
          ))}
          <option value="graduate">{t('rollover.child.graduate')}</option>
        </Select>
      ),
    },
  ];

  return (
    <Stack>
      <Notice tone="info">
        {t('rollover.summary', {
          year: preview.yearName,
          next: preview.nextYearName,
          start: formatDate(preview.nextYearStartDate),
          end: formatDate(preview.yearEndDate),
        })}
      </Notice>

      <Card title={t('rollover.classes')}>
        <DataTable
          caption={t('rollover.classes')}
          columns={classColumns}
          rows={preview.classes}
          rowKey={(c) => c.id}
          empty={<EmptyState icon="🏫" title={t('rollover.classes.empty')} />}
        />
      </Card>

      <Card title={t('rollover.children')}>
        <DataTable
          caption={t('rollover.children')}
          columns={childColumns}
          rows={preview.children}
          rowKey={(c) => c.childId}
          empty={<EmptyState icon="🧒" title={t('rollover.children.empty')} />}
        />
      </Card>

      <Card title={t('rollover.close')}>
        <Stack>
          <Notice tone="warning">
            {t('rollover.close.warning', { terms: preview.openTerms, assignments: preview.assignmentsEnding })}
          </Notice>
          {!ended && <Notice tone="info">{t('rollover.close.notYet', { date: formatDate(preview.canCloseFrom) })}</Notice>}
          {undecided[0] && (
            <Notice tone="warning">{t('rollover.close.undecided', { name: undecided[0].name })}</Notice>
          )}
          {close.isError && <ErrorNotice error={close.error} />}
          <Field label={t('rollover.close.year')}>
            {(props) => <Input {...props} value={preview.nextYearName} readOnly />}
          </Field>
          <Row>
            <Button
              busy={close.isPending}
              busyLabel={t('action.saving')}
              disabled={!ended || undecided.length > 0}
              onClick={() => close.mutate()}
            >
              {t('rollover.close.submit', { year: preview.yearName })}
            </Button>
          </Row>
        </Stack>
      </Card>
    </Stack>
  );
}

function Done({ result, preview }: { result: CloseAcademicYearResponse; preview: RolloverPreviewResponse }) {
  const { t } = useI18n();
  return (
    <Stack>
      <Notice tone="success" title={t('rollover.done.title', { year: preview.yearName, next: preview.nextYearName })}>
        {t('rollover.done.body', {
          classes: result.classesCreated,
          moved: result.childrenMoved,
          graduated: result.childrenGraduated,
          assignments: result.assignmentsEnded,
        })}
      </Notice>
      <Row>
        <Link className="dg-button" to="/academic-years">{t('rollover.done.back')}</Link>
      </Row>
    </Stack>
  );
}

/** A class's plan; a class the form has no entry for is not carried over. */
function planFor(classes: Record<number, ClassPlan>, id: number): ClassPlan {
  return classes[id] ?? { carried: false, name: '' };
}

/** The decision the API proposed for a child, as the form holds it. */
function proposed(child: RolloverChildPreview): Decision {
  return child.graduates || child.toSourceClassId === null ? 'graduate' : `copy:${child.toSourceClassId}`;
}

/** Whether a decision still names something that exists: a copy whose class is still carried over. */
function valid(decision: Decision | undefined, classes: Record<number, ClassPlan>): boolean {
  if (!decision) return false;
  if (!decision.startsWith('copy:')) return true;
  return planFor(classes, Number(decision.slice('copy:'.length))).carried;
}

/** The request the API takes, from what the form holds. */
function plan(
  preview: RolloverPreviewResponse,
  classes: Record<number, ClassPlan>,
  decisions: Record<number, Decision>,
): CloseAcademicYearRequest {
  return {
    nextYearId: preview.nextYearId,
    classes: preview.classes
      .filter((c) => planFor(classes, c.id).carried)
      .map((c) => ({ sourceClassId: c.id, name: planFor(classes, c.id).name.trim() })),
    children: preview.children.map((c) => {
      const decision = decisions[c.childId] ?? proposed(c);
      const [kind, id] = decision.split(':');
      return {
        childId: c.childId,
        graduates: decision === 'graduate',
        toSourceClassId: kind === 'copy' ? Number(id) : null,
        toClassId: kind === 'class' ? Number(id) : null,
      };
    }),
  };
}

/** The API's own title and detail, which say what to do next (NFR-USE-05). */
function ErrorNotice({ error }: { error: unknown }) {
  const { t } = useI18n();
  const described = describeError(error, t);
  return (
    <Notice tone={described.tone} title={described.title} reference={described.reference ?? undefined}>
      {described.body}
    </Notice>
  );
}
